


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type { StandardReportCar, StandardReportPhotoCount } from '@/models/externalReports/standard/spStandardReport';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import type { StandardMtx } from '@/models/apis/externalReports/standard/standardMtxRequest';
import type { ImageCandidateBase as ImageCandidate } from '@/models/apis/image/imageRequest';
import type { StandardReportPhotoParams } from '@/models/apis/externalReports/standard/standardReportRequest';
import type {
  ExternalReportsLabelSp,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import PhotoTaker from '@/components/lib/PhotoTaker.vue';
import standardReportApi from '@/apis/externalReports/standard/standard_report';
import { getImageCandidate } from '@/lib/imageHelper';
import { dtFormat } from '@/lib/dateHelper';
import ConfirmModal from '@/components/lib/ConfirmModal.vue';

interface PhotoMemoState {
  photoType: string;
  photoCounts: StandardReportPhotoCount[];
  imageCandidates: ImageCandidate[];
  errorModalMsg: string;
  pendingPhotosCountBeforeSave: number;
  showCommonErrorModal: boolean;
  showSavePhotosConfirmModal: boolean;
  showRevertSavePhotosConfirmModal: boolean;
  showPhotoSaveErrorModal: boolean;
}

const MAX_PENDING_PHOTOS_COUNT = 8;
const MAX_SAVABLE_PHOTOS_COUNT = 50;

export default defineComponent({
  props: {
    standardCar: {
      type: Object as PropType<StandardReportCar>,
      required: true,
    },
    externalReportAbility: {
      type: Object as PropType<ExternalReportAbility>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
    standardMtxs: {
      type: Array as PropType<StandardMtx[]>,
      required: true,
    },
  },
  components: {
    PhotoTaker,
    ConfirmModal,
  },
  emits: [
    'revertScreen',
    'updateCommon',
    'showErrorOnCommon',
  ],
  setup(props, { emit }) {
    const state = reactive<PhotoMemoState>({
      photoType: '',
      photoCounts: [],
      imageCandidates: [],
      errorModalMsg: '',
      pendingPhotosCountBeforeSave: 0,
      showCommonErrorModal: false,
      showSavePhotosConfirmModal: false,
      showRevertSavePhotosConfirmModal: false,
      showPhotoSaveErrorModal: false,
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const calculatePhotoCount = computed<number>(() => {
      const currentTypePhotoCount = state.photoCounts.find(item => item.photoType === state.photoType)?.count ?? 0;
      return currentTypePhotoCount + state.imageCandidates.length;
    });
    const photoTypes = computed(() => {
      return props.userSettings.choices.photo_types.map(e => {
        return {
          name: e,
          value: e,
        };
      });
    });
    const canSaveAdditionalPhoto = computed<boolean>(() => {
      return calculatePhotoCount.value <= MAX_SAVABLE_PHOTOS_COUNT;
    });
    const canAddPendingPhoto = computed<boolean>(() => {
      return state.imageCandidates.length < MAX_PENDING_PHOTOS_COUNT &&
        calculatePhotoCount.value < MAX_SAVABLE_PHOTOS_COUNT;
    });
    const canSavePhotos = computed<boolean>(() => {
      return !isRequesting.value &&
        canSaveAdditionalPhoto.value &&
        state.imageCandidates.length > 0 &&
        state.photoType !== '';
    });

    const spLabels = computed<ExternalReportsLabelSp>(() => {
      return props.userSettings.sp;
    });
    const fileNamePrefixForDownload = computed<string>(() => {
      return `Standard_${spLabels.value.title.operation_name}_${state.photoType}`;
    });
    const pendingPhotosCount = computed<number>(() => {
      return state.imageCandidates.length;
    });

    const attachImageFile = async(file: File) => {
      if (!canSaveAdditionalPhoto.value) {
        state.errorModalMsg = '添付画像の最大数は' + MAX_SAVABLE_PHOTOS_COUNT + '枚です。';
        state.showCommonErrorModal = true;
        return;
      }

      const imageCandidate = await getImageCandidate(file);
      if (!imageCandidate) return;
      state.imageCandidates.push(imageCandidate);
    };
    const onImageFileAttach = async(evt: Event) => {
      const fileList = (evt.target as HTMLInputElement).files;
      if (!fileList || fileList.length === 0) { return; }
      const file = fileList[0];
      attachImageFile(file);
    };
    const removeImageCandidate = (idx: number) => {
      URL.revokeObjectURL(state.imageCandidates[idx].src);
      state.imageCandidates.splice(idx, 1);
    };
    const tryRevertEditImages = () => {
      if (state.imageCandidates.length === 0) {
        revertScreen();
        return;
      }
      state.showRevertSavePhotosConfirmModal = true;
    };
    const revertScreen = () => {
      emit('revertScreen');
    };
    const sendImagesInner = async() => {
      const standardReport = props.standardCar.report;
      if (standardReport.id === 0 || !state.imageCandidates.length) return;

      state.pendingPhotosCountBeforeSave = pendingPhotosCount.value;

      const mtxSlice = props.standardMtxs.slice(0);
      const mtxLength = mtxSlice.length;
      if (mtxLength === 0) {
        state.errorModalMsg = '位置情報が取得できません。再度操作を行ってください';
        state.showCommonErrorModal = true;
        return;
      }
      const lastMtx = mtxSlice.slice(mtxLength - 1, mtxLength)[0];

      let remainingImageCandidates: ImageCandidate[] = [];
      for (const [index, imageCandidate] of state.imageCandidates.entries()) {
        try {
          const reqObj: StandardReportPhotoParams = {
            ts: dtFormat(lastMtx.ts),
            lat: lastMtx.lat,
            lon: lastMtx.lon,
            photo_type: state.photoType,
            image: imageCandidate.file,
          };
          await doRequest(async() => {
            await standardReportApi.addPhoto(standardReport.id, reqObj);
          });
          URL.revokeObjectURL(imageCandidate.src);
        } catch (e) {
          remainingImageCandidates = state.imageCandidates.slice(index);
          // 一つでも失敗したらユーザーに再送信を促す
          break;
        }
      }
      state.imageCandidates = remainingImageCandidates;
      if (state.imageCandidates.length === 0) {
        emit('updateCommon', props.userSettings.choices.status.working.key, props.standardCar.report);
      }
    };
    const sendImages = async() => {
      await sendImagesInner();

      // 写真の送信失敗処理
      if (state.imageCandidates.length > 0) {
        state.showSavePhotosConfirmModal = false;
        state.showPhotoSaveErrorModal = true;
        return;
      }
      state.showSavePhotosConfirmModal = false;
    };

    onMounted(async() => {
      await doRequest(async() => {
        state.photoCounts = await standardReportApi.getPhotoCount(props.standardCar.report.id);
      }, '写真の取得に失敗しました。再度操作を行ってください');
      state.photoType = photoTypes.value[0].value;
    });

    return {
      state,
      canAddPendingPhoto,
      canSavePhotos,
      fileNamePrefixForDownload,
      isRequesting,
      spLabels,
      photoTypes,
      pendingPhotosCount,
      attachImageFile,
      onImageFileAttach,
      removeImageCandidate,
      revertScreen,
      sendImages,
      tryRevertEditImages,
    };
  },
});
